import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * A custom element that displays a "sale" badge over the product media gallery.
 * Listens for variant select events and shows/hides itself based on whether
 * the newly selected variant has a compare-at price.
 *
 * @extends {Component}
 */
class ProductSaleBadge extends Component {
  /** @type {Element | null} */
  #closestSection = null;

  connectedCallback() {
    super.connectedCallback();
    this.#closestSection = this.closest('.shopify-section, dialog');
    if (!this.#closestSection) return;
    this.#closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (!this.#closestSection) return;
    this.#closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#closestSection = null;
  }

  /**
   * Handles product select event and swaps in the freshly rendered badge state.
   * @param {import('@shopify/events').ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newBadge = html.querySelector(`product-sale-badge[data-block-id="${this.dataset.blockId}"]`);
        if (!newBadge) return;

        this.replaceWith(newBadge);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-sale-badge] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-sale-badge')) {
  customElements.define('product-sale-badge', ProductSaleBadge);
}
